// src/lib/utils.js
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// Helper to escape text for safe insertion into HTML
function escapeHtml(unsafe) {
  if (unsafe === null || unsafe === undefined) return "";
  return String(unsafe)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// Example: backend returns an object `data` with quota_id, label, reason
// const data = { quota_id: "EVAA_quota_123", label: "Male 18-25", reason: "This quota is now full." };

export function buildQuotaClosedHtml(data) {
  const qId = escapeHtml(data.quota_id);
  const label = escapeHtml(data.label);
  const reason = escapeHtml(data.reason || "This quota segment is now full.");

  return `
  <div role="region" aria-labelledby="quota-closed-title" style="
      max-width: 720px;
      margin: 2.5rem auto;
      padding: 2rem 1.75rem;
      border-radius: 16px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.08);
      background: linear-gradient(180deg, #ffffff, #f8faf8);
      font-family: 'Geist Sans', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
      color:#111;
      position: relative;
  ">
    <div style="text-align:center; margin-top:1rem;">
      <div style="font-size:52px; line-height:1; color:#34681A;">🚫</div>
    </div>

    <h2 id="quota-closed-title" style="
        text-align:center;
        margin: 0.5rem 0 1rem;
        font-size:1.5rem;
        font-weight:700;
        color:#34681A;
    ">Survey Closed for This Category</h2>

    <p style="text-align:center; margin:0 0 1.25rem; color:#2A5315; font-size:1.05rem;">
      We're sorry — the quota for <strong>${label}</strong> has been reached.
    </p>

    <div style="
        background:#f4f8f2;
        border:1px solid #d9e7d2;
        border-radius:10px;
        padding:1rem 1.25rem;
        margin: 1rem 0;
        color:#2A5315;
        font-size:0.95rem;
    ">
      <div style="font-weight:600; margin-bottom:0.25rem;">Why you were stopped</div>
      <div>${reason}</div>
    </div>

    <dl style="display:flex; gap:1.25rem; flex-wrap:wrap; justify-content:center; margin:1rem 0 1.5rem; font-size:0.9rem; color:#2A5315;">
      <div>
        <dt style="font-weight:600;">Quota label</dt>
        <dd style="margin:0;">${label}</dd>
      </div>
      <div>
        <dt style="font-weight:600;">Quota id</dt>
        <dd style="margin:0; font-family:'Geist Mono', monospace; color:#3a4a35;">${qId}</dd>
      </div>
    </dl>

    <div style="text-align:center; margin-top:1.25rem;">
      <button
        id="quota-close-back-button"
        style="
          display:inline-block;
          background:#34681A;
          color:white;
          border:none;
          padding:0.6rem 1.1rem;
          border-radius:10px;
          font-weight:600;
          font-size:0.95rem;
          cursor:pointer;
          transition:background 0.2s ease;
        "
        onmouseover="this.style.background='#2A5315'"
        onmouseout="this.style.background='#34681A'"
        onclick="window.location.href = '/'"
        aria-label="Return to homepage"
      >
        Return to homepage
      </button>

      <button
        id="quota-close-contact-button"
        style="
          display:inline-block;
          margin-left:0.7rem;
          background:transparent;
          color:#34681A;
          border:1px solid #d9e7d2;
          padding:0.55rem 1.1rem;
          border-radius:10px;
          font-weight:600;
          cursor:pointer;
        "
        onmouseover="this.style.background='#f0f5ed'"
        onmouseout="this.style.background='transparent'"
        onclick="(function(){ const mail='support@example.com'; window.location.href='mailto:'+mail+'?subject=' + encodeURIComponent('Quota issue: ' + '${qId}'); })()"
        aria-label="Contact support"
      >
        Contact support
      </button>
    </div>

    <p style="text-align:center; margin-top:1.5rem; color:#63775f; font-size:0.85rem;">
      Thank you for your time — we appreciate your participation.
    </p>
  </div>
  `;
}

/**
 * Parse a FastAPI / Pydantic 422 error response into a human-readable message.
 * @param {object} errorResponse - The JSON error response from the backend (e.g. axios error.response.data)
 * @returns {string} A formatted, readable error message
 */
export function parsePydanticError(errorResponse) {
  if (!errorResponse || !errorResponse.detail) {
    return "An unknown error occurred while saving.";
  }

  const messages = errorResponse.detail.map((err) => {
    const { loc = [], msg = "Validation error" } = err;

    // Clean up the "loc" path — remove "body"
    const path = loc.filter((l) => l !== "body");

    // Convert indexes into something more human-readable (e.g., quota #4)
    const formattedPath = path
      .map((part) => {
        if (typeof part === "number") return `#${part + 1}`;
        return part;
      })
      .join(" → ");

    return `${formattedPath ? formattedPath + ": " : ""}${msg}`;
  });

  return messages.join("\n");
}